import { HttpClient, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    ApplicationConfig,
    importProvidersFrom,
    inject,
    isDevMode,
    provideAppInitializer,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withInMemoryScrolling, withPreloading } from '@angular/router';
import { provideFuse } from '@fuse';
import { TranslocoService, provideTransloco } from '@jsverse/transloco';
import { appRoutes } from 'app/app.routes';
import { provideIcons } from 'app/core/icons/icons.provider';
import { MockApiService } from 'app/mock-api';
import { firstValueFrom } from 'rxjs';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';
import { provideEffects } from '@ngrx/effects';
import { NavigationActionTiming, provideRouterStore } from '@ngrx/router-store';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from "@ngrx/store-devtools";
import { ClientAccountsEffect } from '@store/effects/client-accounts.effect';
import { CompanyEffect } from '@store/effects/company.effects';
import { ContextEffect } from '@store/effects/context.effects';
import { UserEffect } from '@store/effects/user.effect';
import { reducers, metaReducers } from '@store/reducers';
import { RouterSerializer } from '@store/router/router.serializer';
import { provideTranslateService, TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { provideNgxMask } from 'ngx-mask';
import { provideHttpInterceptor, globalLoaderInterceptor } from '@core/interceptors/http.provider';
import { provideMomentDateAdapter } from '@angular/material-moment-adapter';
import { OfflinePreloadingStrategy } from '@core/preloading/offline-preloading-strategy';
import { OfflineCacheService } from './services/offline-cache.service';

export function httpLoaderFactory(http: HttpClient) {
    return new TranslateHttpLoader(http, './i18n/', '.json');
}
export const MY_FORMAT = {
    parse: {
        dateInput: ['DD/MM/YYYY', 'DD-MM-YYYY', 'YYYY-MM-DD'],
    },
    display: {
        dateInput: 'DD/MM/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'DD/MM/YYYY',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};
export const appConfig: ApplicationConfig = {
    providers: [
        provideAnimations(),
        provideHttpInterceptor(),
        provideHttpClient(withInterceptors([globalLoaderInterceptor])),
        provideRouter(
            appRoutes,
            withInMemoryScrolling({ scrollPositionRestoration: 'enabled' }),
            withPreloading(OfflinePreloadingStrategy),
        ),

        /** ngrx config */
        provideStore(reducers, { metaReducers }),
        provideRouterStore({
            serializer: RouterSerializer,
            navigationActionTiming: NavigationActionTiming.PostActivation,
        }),
        provideEffects([
            UserEffect,
            ClientAccountsEffect,
            CompanyEffect,
            ContextEffect,
        ]),
        provideStoreDevtools({
            maxAge: 25,
            logOnly: !isDevMode(),
        }),
        provideMomentDateAdapter({
            parse: {
                dateInput: ["DD/MM/YYYY", "DD-MM-YYYY", "YYYY-MM-DD"],
            },
            display: {
                dateInput: "DD/MM/YYYY",
                monthYearLabel: "MMM YYYY",
                dateA11yLabel: "LL",
                monthYearA11yLabel: "MMMM YYYY",
            },
        }),
        // Transloco Config
        provideTransloco({
            config: {
                availableLangs: [
                    {
                        id: 'fr',
                        label: 'french',
                    },
                    {
                        id: 'dz',
                        label: 'arabic',
                    },
                ],
                defaultLang: 'fr',
                fallbackLang: 'dz',
                reRenderOnLangChange: true,
                prodMode: !isDevMode(),
            },
            loader: TranslocoHttpLoader,
        }),
        provideAppInitializer(async () => {
            const translocoService = inject(TranslocoService);
            const offlineCache = inject(OfflineCacheService);
            const defaultLang = translocoService.getDefaultLang();

            // Set active lang
            translocoService.setActiveLang(defaultLang);

            // Fetch default lang and prefetch other critical assets
            const prefetchTask = offlineCache.prefetch([
                '/static/countries.json',
                '/images/flags/flags.png',
                './i18n/fr.json',
                './i18n/dz.json'
            ]);

            await Promise.all([
                firstValueFrom(translocoService.load(defaultLang)),
                prefetchTask
            ]);
        }),
        /** ngx mask */
        provideNgxMask(),

        /** ngx translate  */
        //     provideTranslateService(/*{
        //   loader: {
        //     provide: TranslateLoader,
        //     useFactory: httpLoaderFactory,
        //     deps: [HttpClient],
        //   }}*/),
        importProvidersFrom(
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: httpLoaderFactory,
                    deps: [HttpClient],
                }
            })
        ),
        // Fuse
        provideIcons(),
        provideFuse({
            mockApi: {
                delay: 0,
                service: MockApiService,
            },
            fuse: {
                layout: 'dense',
                scheme: 'light',
                screens: {
                    sm: '500px',
                    md: '700px',
                    lg: '1024px',
                    xl: '1280px',
                },
                theme: 'theme-default',
                themes: [
                    {
                        id: 'theme-default',
                        name: 'Default',
                    },
                ],
            },
        }),
    ],
};
